<template>
    <div id="chart">
        <div class="title">{{ title }}</div>
        <div class="subtitle">
            Number of calls in
            <b>{{
                options.callType.toLowerCase() +
                    (options.callType === "All Categories" ? "*" : "")
            }}</b>
            over time
        </div>
        <div class="description">
            Showing {{ showAvg ? "7-day moving averages " : "call volumes " }}
            <b>{{ options.startDate }}</b> - <b>{{ options.endDate }}</b> in
            <b>{{
                options.city === "All Areas" ? "all areas" : options.city
            }}</b>

            <br />Click on any legend item to focus on that category
        </div>
        <div class="category-tooltip"></div>
        <div id="chart_tooltip"></div>
        <div id="chart_legend">
            <div id="show_minor">
                <span>
                    <input
                        id="show_minor_check"
                        v-model="showMinor"
                        type="checkbox"
                    />
                    <label for="show_minor_check"
                        >Show minor categories in legend</label
                    >
                </span>
                <span style="margin-left: 5px;">
                    <input id="toggle_avg" v-model="showAvg" type="checkbox" />
                    <label for="toggle_avg">Show 7-day moving averages</label>
                </span>
                <a
                    href="#"
                    v-on:click.stop.prevent="
                        $emit('listChange', {
                            source: 'callType',
                            value: 'All Categories',
                        })
                    "
                >
                    <font-awesome-icon icon="undo-alt" />Back to all categories
                </a>
            </div>
            <div id="legend_major"></div>
            <div id="legend_minor" v-show="showMinor"></div>
            <div id="no-data">
                No calls were logged between {{ options.startDate }} and {{ options.endDate }} for calls related to {{ options.callType === "All Categories" ? "all major categories" : options.callType }} in {{ options.city }}.
            </div>
        </div>
        <svg id="svg_chart" />
        <div class="note" v-if="options.callType === 'All Categories'">
            {{ "*" }} Showing calls in major categories only.
        </div>
    </div>
</template>

<script>
import * as d3 from "d3";
import { interpolatePath } from "d3-interpolate-path";
import moment from "moment";
import { toString, categoryDescription } from "./utilities.js";
import { mapState } from "vuex";

const movingAverage = function(values, N) {
    let means = values.map((d, end) => {
        let start = end - N + 1;
        return end < 0
            ? 0
            : Math.round(d3.sum(values.slice(start, end + 1)) / N);
    });
    return means;
};

export default {
    name: "StackChart",
    data() {
        return {
            chart: null,
            height: 400,
            margin: { top: 15, right: 30, bottom: 0, left: 30 },
            top_cat: [
                "Food/Meals",
                "Housing",
                "COVID-19 Hotline",
                "Individual, Family and Community Support",
                "Utility Assistance",
                "Disaster Services",
                "Information Services",
                "COVID-19 Diagnostic Tests",
            ],
            showMinor: false,
            showAvg: true,
        };
    },
    computed: {
        ...mapState({
            options: state => state.options,
        }),
    },
    props: {
        title: String,
    },
    mounted() {
        this.layoutData = this.filterData(this.options, this.showAvg);
        this.chart = this.initChart(this.layoutData);
        window.addEventListener("resize", () =>
            this.chart.updateChart(this.layoutData, this.options)
        );
    },
    methods: {
        filterData(options, avg) {
            if (options.startDate == undefined || options.endDate == undefined)
                return null;

            let high = options.endDate;
            let low = options.startDate;

            if (new moment(high).isBefore(new moment(low))) return null;

            let dates = this.$store.state.rawData.columns;

            let rawData = this.$store.state.rawData.filter(
                d => d.city === options.city
            );

            if (options.callType === "All Categories") {
                rawData = rawData.filter(d => this.top_cat.includes(d.type));
            } else {
                rawData = rawData.filter(d => d.type === options.callType);
            }

            if (avg) {
                rawData = rawData.map(d => ({
                    key: d.type,
                    values: movingAverage(d.values, 7).slice(
                        dates.indexOf(low) == -1 ? 0 : dates.indexOf(low),
                        (dates.indexOf(high) == -1
                            ? dates.length
                            : dates.indexOf(high)) + 1
                    ),
                }));
            } else {
                rawData = rawData.map(d => ({
                    key: d.type,
                    values: d.values.slice(
                        dates.indexOf(low) == -1 ? 0 : dates.indexOf(low),
                        (dates.indexOf(high) == -1
                            ? dates.length
                            : dates.indexOf(high)) + 1
                    ),
                }));
            }

            if (options.callType === "All Categories") {
                this.top_cat.forEach(type => {
                    if (!rawData.map(d => d.key).includes(type)) {
                        rawData.push({
                            key: type,
                            values: new Array(
                                (dates.indexOf(high) == -1
                                    ? dates.length
                                    : dates.indexOf(high)) +
                                    1 -
                                    (dates.indexOf(low) == -1
                                        ? 0
                                        : dates.indexOf(low))
                            ).fill(0),
                        });
                    }
                });
            } else {
                if (!rawData.map(d => d.key).includes(options.callType)) {
                    rawData.push({
                        key: options.callType,
                        values: new Array(
                            (dates.indexOf(high) == -1
                                ? dates.length
                                : dates.indexOf(high)) -
                                (dates.indexOf(low) == -1
                                    ? 0
                                    : dates.indexOf(low) + 1)
                        ).fill(0),
                    });
                }
            }

            let nestedData = rawData[0].values.map(() => {
                return {};
            });

            rawData.forEach(row =>
                row.values.forEach((d, i) => (nestedData[i][row.key] = d))
            );

            let columns =
                options.callType === "All Categories"
                    ? this.top_cat
                    : [options.callType];

            let stackedData = d3.stack().keys(columns)(nestedData);

            return stackedData;
        },

        initChart: function() {
            let data = this.layoutData;
            let filters = this.options;
            let margin = this.margin;
            let major_columns = Object.values(
                this.$parent.callTypes["Major Categories"]
            );
            let minor_columns = Object.values(
                this.$parent.callTypes["Minor Categories"]
            );
            let columns = major_columns.concat(minor_columns);

            let width =
                getProperty("#chart", "width") - margin.left - margin.right;
            let height = this.height - margin.top - margin.bottom;

            let svg = d3
                .select("#svg_chart")
                .attr("width", width + margin.left + margin.right)
                .attr("height", height + margin.left + margin.right);

            let g = svg
                .append("g")
                .attr(
                    "transform",
                    "translate(" + margin.left + "," + margin.top + ")"
                );

            let domainLeft = new moment(filters.startDate).isBefore(
                new moment(filters.dateRange[0])
            )
                ? new Date(filters.dateRange[0])
                : new Date(filters.startDate);

            let domainRight = new moment(filters.endDate).isAfter(
                new moment(filters.dateRange[1])
            )
                ? new Date(filters.dateRange[1])
                : new Date(filters.endDate);

            let x = d3
                .scaleUtc()
                .domain([domainLeft, domainRight])
                .range([0, width]);

            let xAxis = g
                .append("g")
                .attr("id", "x_axis")
                .attr("transform", "translate(0," + (height + margin.top) + ")")
                .call(
                    d3
                        .axisBottom(x)
                        .ticks(7)
                        .tickFormat(d3.utcFormat("%b %-d"))
                );

            let y = d3
                .scaleLinear()
                .domain([
                    0,
                    d3.max([
                        20,
                        d3.max(data.map(d => d3.max(d.map(d1 => d3.max(d1))))) *
                            1.1,
                    ]),
                ])
                .range([height + margin.top, 0]);

            let yAxis = g
                .append("g")
                .attr("id", "y_axis")
                .call(d3.axisLeft(y).ticks(10));

            const color = d3
                .scaleOrdinal()
                .domain(columns)
                .range([
                    "#114d93",
                    "#2eb09f",
                    "#1272e0",
                    "#7ea4cf",
                    "#e6001f",
                    "#fc951e",
                    "#5ae8d5",
                    "#b0c94d",
                    "#ad3b4a",
                    "#eb6073",
                ]);

            let ticks = x.ticks(d3.utcDay);
            let ticksX = ticks.map(x);

            let stacks = g
                .append("g")
                .attr("id", "stack")
                .selectAll("path");

            let paths = stacks
                .data(data)
                .enter()
                .append("path");

            let area = d3
                .area()
                .x(function(d, i) {
                    return x(ticks[i]);
                })
                .y0(d => y(d[0]))
                .y1(d => y(d[1]));

            paths
                .attr("fill", d => color(d.key))
                .on("mousemove", handleMouseOver)
                .on("mouseout", handleMouseOut)
                .attr("class", "area")
                .attr("d", area)
                .attr("opacity", 0)
                .transition()
                .duration(500)
                .delay((d, i) => i * 100)
                .attr("opacity", 1);

            const handleClick = d =>
                this.$emit("listChange", {
                    source: "callType",
                    value: d,
                });

            const handleLegendMouseOver = (d) => {
                let html = `<span>${d}</span>
                <p>${categoryDescription[d]}</p>`

                d3.select(".category-tooltip")
                    .html(html)
                    .style("top", event.pageY - 10 + "px")
                    .style("left", event.pageX + 10 + "px")
                    .style("visibility", "visible");
            }

            const handleLegendMouseOut = () => {
                d3.select(".category-tooltip").style("visibility", "hidden");
            }

            let legendMajor = d3
                .select("#legend_major")
                .selectAll("div")
                .data(major_columns)
                .enter()
                .append("div")
                .attr("class", "legend_item")
                .on("click", handleClick)
                .on("mouseover", handleLegendMouseOver)
                .on("mouseout", handleLegendMouseOut);

            legendMajor
                .append("span")
                .attr("class", "legend_rect")
                .style("background-color", color);

            legendMajor
                .append("span")
                .attr("class", "legend_text")
                .html(d => d.slice(0, 15) + (d.length > 15 ? "..." : ""));

            let legendMinor = d3
                .select("#legend_minor")
                .selectAll("div")
                .data(minor_columns)
                .enter()
                .append("div")
                .attr("class", "legend_item")
                .on("click", handleClick)
                .on("mouseover", handleLegendMouseOver)
                .on("mouseout", handleLegendMouseOut);

            legendMinor
                .append("span")
                .attr("class", "legend_rect")
                .style("background-color", color);

            legendMinor
                .append("span")
                .attr("class", "legend_text")
                .html(d => d.slice(0, 15) + (d.length > 15 ? "..." : ""));

            let interactive = svg
                .append("g")
                .attr("id", "guide")
                .on("mouseover", function() {
                    d3.select(this).style("visibility", "visible");
                    d3.select("#chart_tooltip").style("visibility", "visible");
                })
                .on("mouseout", handleMouseOut);

            interactive
                .append("line")
                .attr("id", "vertline")
                .attr("x1", 0)
                .attr("x2", 0)
                .attr("y1", 0)
                .attr("y2", height + margin.top);

            let circles = interactive.selectAll("circle").data(data);

            circles.enter().append("circle");

            function excludeSegment(a, b) {
                return a.x === b.x;
            }

            function handleMouseOver() {
                // obtain cursor location
                let data = d3
                    .select("#stack")
                    .selectAll("path")
                    .data();

                let mouseX = d3.mouse(this)[0];
                let left = d3.bisectLeft(ticksX, mouseX) - 1;

                let pos =
                    Math.abs(mouseX - ticksX[left]) <
                    Math.abs(mouseX - ticksX[left + 1])
                        ? left
                        : left + 1;

                interactive
                    .attr(
                        "transform",
                        `translate(${margin.left + ticksX[pos]} ${margin.top})`
                    )
                    .style("visibility", "visible");

                interactive
                    .selectAll("circle")
                    .data(data)
                    .join(
                        enter =>
                            enter
                                .append("circle")
                                .attr("r", 3)
                                .attr("cy", d => y(d[pos][1]))
                                .attr("fill", d => color(d.key))
                                .attr("stroke-width", 1)
                                .attr("stroke", "#333"),
                        update =>
                            update
                                .attr("r", 3)
                                .attr("cy", d => y(d[pos][1]))
                                .attr("fill", d => color(d.key))
                                .attr("stroke-width", 1)
                                .attr("stroke", "#333"),
                        exit => exit.remove()
                    );

                let lineLeft = interactive
                    .select("#vertline")
                    .node()
                    .getBoundingClientRect().x;

                let datum = d3.select(this).datum();

                let date = toString(ticks[pos]);
                let type = datum.key;
                let value = +datum[pos].data[type];

                d3.select("#chart_tooltip")
                    .html(`<h6>${date}</h6><span>${type}: </span>${value}`)
                    .style("visibility", "visible")
                    .style("left", lineLeft + 10 + "px")
                    .style("top", event.pageY + 10 + "px");
            }

            function handleMouseOut() {
                interactive.style("visibility", "hidden");

                d3.select("#chart_tooltip").style("visibility", "hidden");
            }

            function updateChart(data, filters) {
                width =
                    getProperty("#chart", "width") - margin.left - margin.right;

                svg.attr("width", width + margin.left + margin.right);

                if (data == null) return;

                let domainLeft = new moment(filters.startDate).isBefore(
                    new moment(filters.dateRange[0])
                )
                    ? new Date(filters.dateRange[0])
                    : new Date(filters.startDate);

                let domainRight = new moment(filters.endDate).isAfter(
                    new moment(filters.dateRange[1])
                )
                    ? new Date(filters.dateRange[1])
                    : new Date(filters.endDate);

                // update x axis
                x = x.domain([domainLeft, domainRight]).range([0, width]);

                xAxis
                    .attr("transform", "translate(0," + (height + margin.top) + ")")
                    .transition()
                    .duration(500)
                    .call(
                        d3
                            .axisBottom(x)
                            .ticks(7)
                            .tickFormat(d3.utcFormat("%b %-d"))
                    );

                // update y axix
                y = y
                    .domain([
                        0,
                        d3.max([
                            20,
                            d3.max(
                                data.map(d => d3.max(d.map(d1 => d3.max(d1))))
                            ) * 1.1,
                        ]),
                    ])
                    .range([height + margin.top, 0]);

                yAxis
                    .attr("id", "y_axis")
                    .transition()
                    .duration(500)
                    .call(d3.axisLeft(y));

                ticks = x.ticks(d3.utcDay);
                ticksX = ticks.map(x);

                let stacks = d3
                    .select("#stack")
                    .selectAll("path")
                    .data(data);

                let t = d3
                    .transition()
                    .duration(500)
                    .delay((d, i) => 100 * i);

                stacks.join(
                    enter =>
                        enter
                            .append("path")
                            .attr("class", "area")
                            .attr("fill", d => color(d.key))
                            .attr("d", area)
                            .attr("opacity", 0)
                            .on("mousemove", handleMouseOver)
                            .on("mouseout", handleMouseOut)
                            .call(enter =>
                                enter.transition(t).attr("opacity", 1)
                            ),
                    update =>
                        update
                            .attr("fill", d => color(d.key))
                            .call(update =>
                                update
                                    .transition()
                                    .duration(500)
                                    .attr("opactiy", 1)
                                    .attrTween("d", function(d) {
                                        let previous = d3
                                            .select(this)
                                            .attr("d");
                                        let current = area(d);
                                        return interpolatePath(
                                            previous,
                                            current,
                                            excludeSegment
                                        );
                                    })
                            ),
                    exit =>
                        exit
                            .transition()
                            .duration(500)
                            .attr("opacity", 0)
                            .remove()
                );

                if (d3.sum(data, d1 => d3.sum(d1, d2 => d3.sum(d2))) == 0) {
                    d3.select("#no-data").style("display", "block");
                    svg.style("background-color", "#eee");
                } else {
                    d3.select("#no-data").style("display", "none");
                    svg.style("background-color", "#fff");
                }
            }

            function getProperty(id, property) {
                let width = d3.select(id).style(property);
                width = +width.substring(0, width.length - 2);
                return width;
            }

            return {
                updateChart,
            };
        },
    },
    watch: {
        options: {
            deep: true,
            handler(newOptions) {
                this.layoutData = this.filterData(newOptions, this.showAvg);
                this.chart.updateChart(this.layoutData, newOptions);
            },
        },
        showAvg: {
            handler(newAvg) {
                this.layoutData = this.filterData(this.options, newAvg);
                this.chart.updateChart(this.layoutData, this.options);
            },
        },
    },
};
</script>

<style lang="sass">
$header-font: trade-gothic-next, Century Gothic, serif
$text-font: "Roboto", Helvetica Neue, Arial, sans-serif

#chart
  margin-top: 30px
  min-width: 370px
  max-width: 750px
  padding: 0
  flex: 60%

#svg_chart .legendtext
  color: #222
  font-size: 0.6rem

#chart_legend
  margin-top: 20px

#legend_major, #legend_minor
  margin: 10px 5px
  display: grid
  grid-gap: 2px
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr))

  & .legend_item
    font-size: 0.8rem
    cursor: pointer

#chart_legend .legend_rect
  display: inline-block
  padding: 2px
  width: 10px
  height: 10px
  border-radius: 5px

#no-data
  padding: 2px
  color: #fe230a
  display: none

#show_minor input
  border-width: 1px
  border-radius: 3px

#show_minor label
  font-size: 0.9rem
  padding: 0 0 3px 5px

#show_minor a
  color: #222
  font-size: 0.9rem
  text-decoration: none
  padding: 0 0 3px 5px
  cursor: pointer

#show_minor a:hover
  color: #fe230a
  fill: #fe230a

#show_minor svg
  padding: 0 5px

#chart_legend .legend_text
  display: inline-block
  padding-left: 8px
  vertical-align: top

#chart_tooltip
  visibility: hidden
  position: absolute
  display: block
  background-color: rgba(255,255,255,0.5)
  font-size: 0.85rem
  border-radius: 10px
  padding: 10px

#chart_tooltip h6
  font-size: 0.90rem
  font-style: none
  font-weight: bold
  padding: 5px 5px 5px 0px
  margin: 0px

.legend_title
  font-size: 0.90rem
  font-weight: 500
  margin: 8px 0

#stack .area:hover
  opacity: 0.5
  transition: opacity 0.5s

#guide
  visibility: hidden

#guide circle
  r: 3px
  stroke: #333
  stroke-width: 1px
  cursor: pointer

  &:hover
    r: 5px
    stroke: #ccc

#guide line
  stroke: #333
  stroke-width: 1px

#x_axis, #y_axis
  font-size: 0.8rem

.category-tooltip
  background: rgba(0, 0, 0, 0.5)
  width: 220px
  padding: 10px
  border-radius: 4px
  position: absolute
  z-index: 30
  visibility: hidden
  color: white
  font-family: $text-font
  font-size: 0.9rem
  text-align: left
  overflow: wrap

  & span
    margin-bottom: 10px
    display: block
    color: white
    font: bold 1rem $text-font
    text-align: left

  & p
    margin-bottom: 0px
</style>
